import type { CalendarEvent } from 'paintscout'
import { track } from 'shared/util/analytics'

/**
 * Where a calendar event save started. `drag` covers both moving and resizing a block on a
 * calendar view.
 */
export type CalendarEventSource =
  | 'calendar'
  | 'production_calendar'
  | 'drag'
  | 'crew_change'
  | 'progress'
  | 'schedule_job_dialog'
  | 'new_button'
  | 'deal'
  | 'contact'
  | 'company'
  | 'event_table'

/** Number of crew members assigned to a job. `assignedUsers` is a record or an array (ENG-734). */
function getAssignedCount(event: CalendarEvent): number {
  const assigned = event.assignedUsers as unknown
  if (Array.isArray(assigned)) return assigned.length
  if (assigned && typeof assigned === 'object') {
    return Object.values(assigned as Record<string, { assigned?: boolean }>).filter((v) => v?.assigned).length
  }
  return 0
}

/**
 * Send the PostHog event for a calendar event that saved. Call it only after `saveEvent` resolves.
 *
 * Giving a production job its first dates sends `job_scheduled`, not `event_created`/`event_updated`,
 * so one action sends one event. An unscheduled job from `unscheduledJobs` is a placeholder until
 * that save, so `wasUnscheduled` counts as new.
 */
export function trackCalendarEventSaved(
  event: CalendarEvent,
  {
    source,
    isNew = false,
    wasUnscheduled = false
  }: { source: CalendarEventSource; isNew?: boolean; wasUnscheduled?: boolean }
) {
  if (event.canceled) {
    track('event_deleted', { eventId: event.id, eventType: event.eventType, production: !!event.production, source })
    return
  }

  if (event.production && event.start && (isNew || wasUnscheduled)) {
    track('job_scheduled', {
      eventId: event.id,
      dealId: event.deal?.id || undefined,
      crewId: event.crew,
      crewCount: getAssignedCount(event),
      source
    })
    return
  }

  track(isNew ? 'event_created' : 'event_updated', {
    eventId: event.id,
    eventType: event.eventType,
    dealId: event.deal?.id || undefined,
    production: !!event.production,
    isAllDay: !!event.allDay,
    source
  })
}
